/**
 * @license Copyright (c) 2014-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';

import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
//import { Autosave } from '@ckeditor/ckeditor5-autosave';
import {
   Bold,
   Code,
   Italic,
   Strikethrough,
   Subscript,
   Superscript,
   Underline
} from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { CloudServices } from '@ckeditor/ckeditor5-cloud-services';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
import type { EditorConfig } from '@ckeditor/ckeditor5-core';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace';
import { FontBackgroundColor, FontColor, FontFamily, FontSize } from '@ckeditor/ckeditor5-font';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { Highlight } from '@ckeditor/ckeditor5-highlight';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import {
   //FullPage,
   GeneralHtmlSupport,
   HtmlComment
} from '@ckeditor/ckeditor5-html-support';
import {
   Image,
   ImageCaption,
   ImageInsert,
   ImageResize,
   ImageStyle,
   ImageToolbar,
   ImageUpload
} from '@ckeditor/ckeditor5-image';
import { SimpleUploadAdapter } from '@ckeditor/ckeditor5-upload';
import { Indent, IndentBlock } from '@ckeditor/ckeditor5-indent';
import { AutoLink, LinkImage } from '@ckeditor/ckeditor5-link';
import { List, ListProperties, TodoList } from '@ckeditor/ckeditor5-list';
import { MediaEmbed, MediaEmbedToolbar } from '@ckeditor/ckeditor5-media-embed';
import { PageBreak } from '@ckeditor/ckeditor5-page-break';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { SelectAll } from '@ckeditor/ckeditor5-select-all';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import {
   SpecialCharacters,
   SpecialCharactersArrows,
   SpecialCharactersCurrency,
   SpecialCharactersEssentials,
   SpecialCharactersLatin,
   SpecialCharactersMathematical,
   SpecialCharactersText
} from '@ckeditor/ckeditor5-special-characters';
import {
   Table,
   TableCaption,
   TableCellProperties,
   TableColumnResize,
   TableProperties,
   TableToolbar
} from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import { WordCount } from '@ckeditor/ckeditor5-word-count';

import ManageMedia from '../CustomPlugins/ManageMedia2.js';
import CustomLink from '../CustomPlugins/CustomLink.js';
import FileManagerPopup from '../CustomPlugins/FileManagerPopup.js';

import FullScreen from '../CustomPlugins/FullScreen.js';

import '../CustomCss/CustomContent.css';

class Editor extends ClassicEditor {
   public static override builtinPlugins = [
      Alignment,
      AutoLink,
      Autoformat,
      //Autosave,
      BlockQuote,
      Bold,
      CloudServices,
      Code,
      CodeBlock,
      Essentials,
      FindAndReplace,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      //FullPage,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      HtmlComment,
      HtmlEmbed,
      Image,
      ImageCaption,
      ImageInsert,
      ImageResize,
      ImageStyle,
      ImageToolbar,
      ImageUpload,
      SimpleUploadAdapter,
      Indent,
      IndentBlock,
      Italic,
      //Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      MediaEmbedToolbar,
      PageBreak,
      Paragraph,
      PasteFromOffice,
      RemoveFormat,
      SelectAll,
      SourceEditing,
      SpecialCharacters,
      SpecialCharactersArrows,
      SpecialCharactersCurrency,
      SpecialCharactersEssentials,
      SpecialCharactersLatin,
      SpecialCharactersMathematical,
      SpecialCharactersText,
      Strikethrough,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      TodoList,
      Underline,
      WordCount,
      ManageMedia,
      CustomLink,
      FileManagerPopup,

      FullScreen
   ];

   public static override defaultConfig: EditorConfig = {
      toolbar: {
         items: [
            'heading',
            '|',
            'style',
            'textPartLanguage',
            'bold',
            'italic',
            //'link',
            'bulletedList',
            'numberedList',
            'alignment',
            'fontBackgroundColor',
            'fontColor',
            'fontFamily',
            'fontSize',
            'highlight',
            'horizontalLine',
            'pageBreak',
            'removeFormat',
            'selectAll',
            'showBlocks',
            'specialCharacters',
            'restrictedEditingException',
            'strikethrough',
            'underline',
            '|',
            'todoList',
            'outdent',
            'indent',
            '|',
            'imageUpload',
            'blockQuote',
            'insertTable',
            'mediaEmbed',
            'undo',
            'redo',
            '|',
            'code',
            'codeBlock',
            'findAndReplace',
            'htmlEmbed',
            'imageInsert',
            'subscript',
            'superscript',
            'sourceEditing',
            '|',
            'fileManagerPopup',
            'fullScreen'
         ]
      },
      language: 'it',
      image: {
         toolbar: [
            'imageTextAlternative',
            'toggleImageCaption',
            'imageStyle:inline',
            'imageStyle:block',
            'imageStyle:side',
            'linkImage'
         ]
      },
      table: {
         contentToolbar: [
            'tableColumn',
            'tableRow',
            'mergeTableCells',
            'tableCellProperties',
            'tableProperties'
         ]
      }
   };


   // Override setData method
   public override setData(data: string): void {
      super.setData(""); // Per problema visualizzazione immagine su EsseCi in comunicazioni
      super.setData(data);

      const sourceEditingPlugin = this.plugins.get('SourceEditing');
      if (sourceEditingPlugin) {
         if (sourceEditingPlugin.isSourceEditingMode) {
            sourceEditingPlugin.isSourceEditingMode = false;
            sourceEditingPlugin.isSourceEditingMode = true;
         }
      }
   }


   // Override getData method
   public override getData() {

      var data = super.getData();
      if (!data.includes("ow-ck-content") && data != "")
         data = "<div class='ow-ck-content'>" + data + "</div>"; // Aggiunge div contenitore per identificazione in frontend

      return data;
   }


   // Override updateSourceElement method
   public override updateSourceElement() {
      this.setData(this.getData());

      super.updateSourceElement();
   }

}

export default { Editor };
